import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Tooltip } from "@mui/material";
import { serializeDateTime } from "@notemeal/utils-date-time";
import { DurationSelectType, TimeDurationSelectSchema } from "apps/web/src/componentLibrary/DurationSelect/FormSchema";
import { CalendarEvent } from "apps/web/src/components/Calendar/Week/utils";
import { ActivityType, MealType } from "apps/web/src/types";
import addMinutes from "date-fns/addMinutes";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { NewEvent } from "../../../Form/utils";
import ActionPopoverBase from "../ActionBase";
import { ACTIVITY, MEAL, NewEventFormFields } from "./NewEventFormFields";

interface MealPlanCalendarEventNewContentProps {
  newEvent: CalendarEvent;
  anchorEl: HTMLElement;
  onClose: () => void;
  onConfirm: (event: NewEvent) => void;
}

const MealPlanCalendarEventNewContent = ({ newEvent, anchorEl, onClose, onConfirm }: MealPlanCalendarEventNewContentProps) => {
  const [selectedTab, setSelectedTab] = useState(MEAL);
  const [mealType, setMealType] = useState<MealType>("breakfast");
  const [activityType, setActivityType] = useState<ActivityType>("practice");
  const [eventName, setEventName] = useState<string>("");

  const form = useForm<DurationSelectType>({
    defaultValues: {
      start: newEvent.start,
      end: addMinutes(newEvent.start, newEvent.durationInMinutes),
    },
    resolver: zodResolver(TimeDurationSelectSchema()),
  });

  const hasFormErrors = form.formState.isSubmitted && !form.formState.isValid;

  const handleConfirm = (duration: DurationSelectType) => {
    const { start, end } = duration;
    const startDatetime = new Date(newEvent.start);
    startDatetime.setHours(start.getHours(), start.getMinutes());
    const endDatetime = new Date(newEvent.start);
    endDatetime.setHours(end.getHours(), end.getMinutes());

    const eventPayload = {
      name: eventName ? eventName : selectedTab === MEAL ? mealType : activityType,
      start: serializeDateTime(startDatetime),
      end: serializeDateTime(endDatetime),
    };

    onClose();
    if (selectedTab === MEAL) {
      onConfirm({
        ...eventPayload,
        type: mealType,
        __typename: "Meal",
      });
    } else {
      onConfirm({
        ...eventPayload,
        type: activityType,
        __typename: "Activity",
      });
    }
  };

  return (
    <ActionPopoverBase
      anchorEl={anchorEl}
      onClose={onClose}
      title={"Add Event"}
      variant="xl"
      buttons={
        <>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Tooltip title={hasFormErrors ? "Resolve the errors above to continue" : ""}>
            <Button onClick={form.handleSubmit(handleConfirm)}>Save</Button>
          </Tooltip>
        </>
      }
    >
      <NewEventFormFields
        selectedTab={selectedTab}
        onSelectedTab={setSelectedTab}
        eventName={eventName}
        onEventNameChange={setEventName}
        mealType={mealType}
        onMealTypeChange={setMealType}
        activityType={activityType}
        onActivityTypeChange={setActivityType}
        control={form.control}
        errors={form.formState.errors}
      />
    </ActionPopoverBase>
  );
};

export default MealPlanCalendarEventNewContent;
